/**
 * Sidebar menu search. Folders stay folders (with children); only leaves with a page open a tab.
 */

/** Má používateľ priame oprávnenie na túto položku (alebo nemá položka permission a dedí od rodiča). */
export function itemAllowedForChildren(item, userPermissions, parentAllowed) {
  const perm = item.permission;
  if (perm === undefined || perm === null) return parentAllowed;
  return Array.isArray(perm) ? perm.some((p) => userPermissions?.includes(p)) : userPermissions?.includes(perm);
}

/**
 * Položka je viditeľná, ak:
 * - má používateľ jej permission (alebo aspoň jedno z nich), alebo
 * - položka nemá permission a rodič má povolený prístup (parentAllowed), alebo
 * - má deti a aspoň jedno dieťa je viditeľné (deti pri rekurzii nededia – parentAllowed false).
 */
export function isItemVisible(item, userPermissions, parentAllowed = true) {
  const perm = item.permission;
  const hasDirectPermission =
    perm === undefined || perm === null
      ? null
      : (Array.isArray(perm) ? perm.some((p) => userPermissions?.includes(p)) : userPermissions?.includes(perm));

  if (hasDirectPermission === true) return true;
  if (hasDirectPermission === null && parentAllowed) return true;
  if (hasDirectPermission === false && item.children?.length) {
    return item.children.some((child) => isItemVisible(child, userPermissions, false));
  }
  return false;
}

function shouldUseCompoundTabTitle(item, parentTitle, tr) {
  if (!parentTitle) return false;
  const rawTitle = String(item?.title || item?.page || '').trim();
  const translatedTitle = String(tr(rawTitle)).toLowerCase();
  return (
    item?.tabCompoundFromParent === true ||
    rawTitle.toLowerCase().startsWith('podľa') ||
    /\.Podla[A-Z]/.test(rawTitle) ||
    translatedTitle.startsWith('podľa') ||
    translatedTitle.startsWith('by ') ||
    translatedTitle.startsWith('nach ')
  );
}

/** Názov karty: tabName, inak title/page. Pri tabCompoundFromParent alebo "podľa / by …" vráti "Rodič podľa ...". */
export function getTabName(item, parentTitle, t) {
  const tr = typeof t === 'function' ? t : (k) => k;
  const name = tr(item.tabName || item.title || item.page);
  if (!shouldUseCompoundTabTitle(item, parentTitle, tr)) return name;
  const suffix = name.charAt(0).toLowerCase() + name.slice(1);
  return `${tr(parentTitle)} ${suffix}`;
}

/**
 * Semantic key for live TabPanelList titles. Null when compound (snapshot-only).
 */
export function getTabTranslationKey(item, parentTitle, t) {
  const tr = typeof t === 'function' ? t : (k) => k;
  if (shouldUseCompoundTabTitle(item, parentTitle, tr)) return null;
  const key = item?.tabName || item?.title || item?.page;
  return key ? String(key) : null;
}

export function isMenuFolder(item) {
  return Array.isArray(item?.children);
}

export function createMenuTabAction(item, parentTitle, t) {
  if (!item?.page) return null;
  if (isMenuFolder(item)) return null;
  const name = getTabName(item, parentTitle, t);
  const translationKey = getTabTranslationKey(item, parentTitle, t);
  const action = {
    type: 'create_new_tab',
    name,
    legacyLabel: name,
    // Always pass field so locale can re-resolve when menu supplies a key; undefined is ignored by reducer.
    translationKey: translationKey || undefined,
    page: item.page,
    mask: item?.mask,
    props: item?.props ?? {},
  };
  return action;
}

export function filterMenuItemsByQuery(arr, query, translate) {
  const tr = typeof translate === 'function' ? translate : (k) => k;
  const q = String(query || '').toLowerCase();
  if (!q) return arr;
  return (arr || []).reduce((acc, item) => {
    if (isMenuFolder(item) && item.children.length) {
      const filtered = filterMenuItemsByQuery(item.children, query, tr);
      const label = String(tr(item.title || item.page || '')).toLowerCase();
      if (filtered.length) return [...acc, { ...item, children: filtered }];
      if (label.includes(q)) return [...acc, item];
      return acc;
    }
    const label = String(tr(item.title || item.page || '')).toLowerCase();
    return label.includes(q) ? [...acc, item] : acc;
  }, []);
}

export function collectMenuSearchTabActions(items, t, parentTitle) {
  const actions = [];
  (items || []).forEach((item) => {
    if (isMenuFolder(item)) {
      actions.push(...collectMenuSearchTabActions(item.children, t, item.title || item.page || parentTitle));
      return;
    }
    const action = createMenuTabAction(item, parentTitle, t);
    if (action) actions.push(action);
  });
  return actions;
}
